<!-- Account and workspace menu content, shown in a popover on larger screens and an action sheet on phones.
     It pads itself, so its containers have no padding and the divider spans their full width. -->
<template>
    <!-- The action sheet's handle already spaces the top on phones; the bottom adds the safe area, which is 0 in the popover -->
    <div class="flex flex-col text-left pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:pt-3 sm:pb-3">
        <div class="flex flex-col gap-0.5 px-3">
            <!-- User -->
            <div class="flex items-center gap-3 px-2 pb-3 sm:pt-1">
                <Avatar :alt="displayName" :initials="userInitials" :src="user?.profilePicture?.url" class="size-10 shrink-0 border-2 text-lg" />
                <div class="min-w-0 flex-1">
                    <div class="truncate text-[15px] font-semibold text-default">{{ displayName }}</div>
                    <div v-if="email && email !== displayName" class="truncate text-xs text-sub">{{ email }}</div>
                </div>
            </div>

            <!-- Workspaces -->
            <template v-if="workspaceRows.length">
                <div class="px-2 pt-2 pb-1 text-xs font-semibold text-sub">Workspaces</div>
                <!-- Shadows fade in at the top and bottom while there is more to scroll, like ScrollableContainer in @tapp.online/ui -->
                <div class="relative">
                    <div
                        class="pointer-events-none absolute -inset-x-3 top-0 z-10 h-6 bg-gradient-to-b from-surface-border/50 to-transparent mix-blend-multiply"
                        :style="{ opacity: topShadowOpacity }"
                    />
                    <div
                        class="pointer-events-none absolute -inset-x-3 bottom-0 z-10 h-6 bg-gradient-to-t from-surface-border/50 to-transparent mix-blend-multiply"
                        :style="{ opacity: bottomShadowOpacity }"
                    />
                    <div ref="workspaceList" class="max-h-[40vh] overflow-y-auto sm:max-h-80">
                        <div ref="workspaceContent" class="flex flex-col gap-0.5 pb-2">
                            <button
                                v-for="row in workspaceRows"
                                :key="row.key"
                                type="button"
                                class="flex w-full cursor-pointer items-center gap-3 rounded-2xl p-2 text-left transition-colors"
                                :class="[row.isActive ? 'bg-gold-100' : 'hover:bg-surface-background', row.kind === 'child' ? 'pl-5' : '']"
                                :data-active="row.isActive || undefined"
                                :disabled="switching"
                                @click="select(row)"
                            >
                                <span
                                    v-if="row.kind === 'child'"
                                    class="-mr-1 h-4 w-4 shrink-0 self-center rounded-bl-md border-b-2 border-l-2 border-surface-border"
                                    aria-hidden="true"
                                />
                                <Avatar :initials="workspaceInitials(rowName(row))" class="size-10 shrink-0 border-2 text-lg" />
                                <div class="min-w-0 flex-1">
                                    <div class="truncate text-sm font-semibold text-default">{{ rowName(row) }}</div>
                                    <div v-if="rowShortCode(row)" class="truncate text-[11px] text-sub">{{ rowShortCode(row) }}</div>
                                </div>
                                <Chip v-if="row.isActive" label="Current" size="sm" variant="outline-light" />
                            </button>
                        </div>
                    </div>
                </div>
            </template>
        </div>

        <div class="border-t border-surface-border mb-2" />

        <!-- Actions -->
        <div class="flex flex-col gap-0.5 px-3">
            <button v-if="userCanSwitchCompany || pendingInvites.length" type="button" :class="actionClasses" @click="goToWorkspaces">
                <Icon type="connections" class="w-5" />
                <span class="flex-1">My workspaces</span>
                <Badge v-if="pendingInvites.length" :label="pendingInvites.length" variant="alert" size="sm" />
            </button>
            <button v-if="dashboardUrl" type="button" :class="actionClasses" @click="openDashboard">
                <Icon type="dashboard" class="w-5" />
                <span class="flex-1">Open dashboard</span>
            </button>
            <button type="button" :class="actionClasses" @click="handleSignOut">
                <Icon type="signOut" class="w-5" />
                <span class="flex-1">Sign out</span>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { Avatar, Badge, Chip, Icon } from '@tapp.online/ui'
    import { useResizeObserver, useScroll } from '@vueuse/core'
    import type { WorkspaceRow } from '@/composables/useWorkspaceSwitch'

    const emit = defineEmits<{
        close: []
    }>()

    const route = useRoute()
    const { user, sessionUser, userCanSwitchCompany, signOut } = useAuth()
    const { workspacesPath, redirectQueryKey, dashboardUrl } = useAuthConfig()
    const { pendingInvites } = usePendingInvites()
    const { workspaceRows, switching, switchWorkspace, workspaceInitials, rowName, rowShortCode } = useWorkspaceSwitch()

    // 44px rows at 15px on phones, 40px at 14px in the popover
    const actionClasses =
        'flex h-11 w-full cursor-pointer items-center gap-3 rounded-2xl px-2 text-left text-[15px] font-medium text-default transition-colors hover:bg-surface-background sm:h-10 sm:text-sm'

    const displayName = computed(() => user.value?.fullName || sessionUser.value?.name || sessionUser.value?.email || 'Account')
    const email = computed(() => sessionUser.value?.email)
    const userInitials = computed(() => {
        if (user.value?.initials) return user.value.initials
        const name = sessionUser.value?.name || sessionUser.value?.email || ''
        return name
            .split(/[\s@._-]+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((part) => part[0]?.toUpperCase() ?? '')
            .join('')
    })

    // Centre the active workspace when the list overflows. Scrolls only the list, not the page or popover.
    const workspaceList = useTemplateRef<HTMLElement>('workspaceList')
    const scrollToActive = () => {
        const list = workspaceList.value
        const active = list?.querySelector<HTMLElement>('[data-active]')
        if (!list || !active || list.scrollHeight <= list.clientHeight) return
        const offset = active.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop
        list.scrollTop = offset - (list.clientHeight - active.offsetHeight) / 2
    }
    watch(
        () => workspaceRows.value.findIndex((row) => row.isActive),
        () => nextTick(scrollToActive),
        { immediate: true }
    )

    // Shadow opacity follows the distance left to scroll, reaching full strength after 60px
    const SHADOW_FADE_DISTANCE = 60
    const workspaceContent = useTemplateRef<HTMLElement>('workspaceContent')
    const { y: listScrollTop } = useScroll(workspaceList)
    const listMaxScroll = ref(0)
    useResizeObserver([workspaceList, workspaceContent], () => {
        const list = workspaceList.value
        if (list) listMaxScroll.value = list.scrollHeight - list.clientHeight
    })
    const shadowFade = (distance: number) => Math.min(Math.max(distance / SHADOW_FADE_DISTANCE, 0), 1)
    const topShadowOpacity = computed(() => shadowFade(listScrollTop.value))
    const bottomShadowOpacity = computed(() => shadowFade(listMaxScroll.value - listScrollTop.value))

    const select = async (row: WorkspaceRow) => {
        const switched = await switchWorkspace(row)
        if (switched) emit('close')
    }

    // The overview sends the user back to the report they opened the menu from
    const goToWorkspaces = () => {
        emit('close')
        navigateTo({ path: workspacesPath || '/workspaces', query: { [redirectQueryKey]: route.fullPath } })
    }

    const openDashboard = () => {
        emit('close')
        if (dashboardUrl) window.open(dashboardUrl, '_blank', 'noopener')
    }

    const handleSignOut = async () => {
        emit('close')
        await signOut()
    }
</script>
